import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useLanguage } from '../context/LanguageContext';
import { contactInfo } from '../mock';
import { Phone, Mail, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    fullName: '',
    phoneNumber: '',
    email: '',
    pickupLocation: '',
    destination: '',
    dateTime: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, {
        ...formData,
        language: language
      });

      if (response.data.success) {
        toast.success(t.contact.form.successMessage);
        setFormData({
          fullName: '',
          phoneNumber: '',
          email: '',
          pickupLocation: '',
          destination: '',
          dateTime: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send request. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen py-20 bg-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl md:text-5xl font-bold text-[#1F3C88] text-center mb-12">
          {t.contact.title}
        </h1>

        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Direct Contact Section */}
          <div className="space-y-6">
            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-[#1F3C88]">
                  {t.contact.directContact}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 mb-6">{t.contact.reachUs}</p>
                <div className="space-y-4">
                  <a
                    href={`https://wa.me/${contactInfo.whatsapp.replace(/\s+/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-4 bg-[#FBC02D] text-[#1F3C88] rounded-lg hover:bg-[#f9b200] transition-colors group"
                  >
                    <div className="flex items-center space-x-3">
                      <MessageCircle className="h-6 w-6" />
                      <div>
                        <div className="font-semibold">{t.contact.whatsapp}</div>
                        <div className="text-sm">{contactInfo.whatsapp}</div>
                      </div>
                    </div>
                  </a>

                  <a
                    href={`tel:${contactInfo.phone}`}
                    className="flex items-center justify-between p-4 bg-white border-2 border-[#1F3C88] text-[#1F3C88] rounded-lg hover:bg-[#1F3C88] hover:text-white transition-colors group"
                  >
                    <div className="flex items-center space-x-3">
                      <Phone className="h-6 w-6" />
                      <div>
                        <div className="font-semibold">{t.contact.phone}</div>
                        <div className="text-sm">{contactInfo.phone}</div>
                      </div>
                    </div>
                  </a>

                  <a
                    href={`mailto:${contactInfo.email}`}
                    className="flex items-center justify-between p-4 bg-white border-2 border-[#1F3C88] text-[#1F3C88] rounded-lg hover:bg-[#1F3C88] hover:text-white transition-colors group"
                  >
                    <div className="flex items-center space-x-3">
                      <Mail className="h-6 w-6" />
                      <div>
                        <div className="font-semibold">{t.contact.email}</div>
                        <div className="text-sm">{contactInfo.email}</div>
                      </div>
                    </div>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form Section */}
          <div>
            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-[#1F3C88]">
                  {t.contact.requestOfferTitle}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="fullName">{t.contact.form.fullName}</Label>
                    <Input
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phoneNumber">{t.contact.form.phoneNumber}</Label>
                    <Input
                      id="phoneNumber"
                      name="phoneNumber"
                      type="tel"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">{t.contact.form.email}</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="pickupLocation">{t.contact.form.pickupLocation}</Label>
                    <Input
                      id="pickupLocation"
                      name="pickupLocation"
                      value={formData.pickupLocation}
                      onChange={handleChange}
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="destination">{t.contact.form.destination}</Label>
                    <Input
                      id="destination"
                      name="destination"
                      value={formData.destination}
                      onChange={handleChange}
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="dateTime">{t.contact.form.dateTime}</Label>
                    <Input
                      id="dateTime"
                      name="dateTime"
                      type="datetime-local"
                      value={formData.dateTime}
                      onChange={handleChange}
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">{t.contact.form.message}</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="mt-1"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#FBC02D] text-[#1F3C88] hover:bg-[#f9b200] font-semibold py-6 text-lg"
                  >
                    {isSubmitting ? 'Sending...' : t.contact.form.submit}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};